<template>
  <section class="home-section" aria-labelledby="top-picks-title">
    <div class="section-header">
      <h2 id="top-picks-title">Top Picks</h2>
    </div>

    <div v-if="articleState.isLoading || isLoading" class="card-skeletons">
      <v-skeleton-loader
        v-for="i in 3"
        :key="i"
        class="card-skeleton"
        type="image, article"
      />
    </div>

    <v-sheet
      v-else-if="!picks.length"
      class="home-empty"
      color="surface"
      elevation="0"
    >
      <v-icon color="accent" size="32">mdi-star-outline</v-icon>
      <p>Curated picks will appear here soon.</p>
    </v-sheet>

    <div v-else class="picks-grid">
      <ArticleCard
        v-for="article in picks"
        :key="article.id"
        :article="article"
        compact
      />
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axios from "axios";

import ArticleCard from "@/components/ArticleCard.vue";
import { articleState } from "@/store/articlesStore";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3000"
    : "https://api.lwyr.ca";

const pickIds = ["3190", "3127", "143"];
const picks = ref([]);
const isLoading = ref(true);

onMounted(loadPicks);

async function loadPicks() {
  isLoading.value = true;

  try {
    const responses = await Promise.all(
      pickIds.map((id) => axios.get(`${API_URL}/api/article/${id}`))
    );

    picks.value = responses.map(({ data }) => normalizeArticle(data));
  } catch (error) {
    console.error("Failed to load top picks", error);
  } finally {
    isLoading.value = false;
  }
}

function normalizeArticle(article = {}) {
  const id = article.id ?? article.article_id ?? "";
  const numericId = Number(id);
  const stockImage = Number.isFinite(numericId) ? (numericId % 7) + 1 : 1;

  return {
    ...article,
    id,
    title: article.title ?? "",
    description: article.description || article.summary || "",
    urlToImage:
      article.urlToImage?.trim() ||
      article.image_url ||
      `https://lwyr.ca/img/stock/${stockImage}.jpg`,
    source: article.source || article.source_name || article.name || "",
    type: article.type || "",
    published:
      article.published || article.publishedAt || article.published_at || "",
    views: article.views ?? 0,
  };
}
</script>

<style scoped>
.home-section {
  margin-bottom: 64px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-header h2 {
  margin: 0;
  color: rgb(var(--v-theme-on-surface));
  font-size: clamp(1.55rem, 3vw, 2rem);
  font-weight: 850;
  line-height: 1.2;
  text-transform: uppercase;
}

.picks-grid,
.card-skeletons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.picks-grid :deep(.library-card) {
  height: 100%;
}

.card-skeleton {
  min-height: 500px;
  border-radius: 8px;
}

.home-empty {
  display: grid;
  gap: 12px;
  place-items: center;
  min-height: 220px;
  padding: 32px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 8px;
  color: rgba(var(--v-theme-on-surface), 0.68);
  text-align: center;
}

.home-empty p {
  margin: 0;
}

@media (max-width: 900px) {
  .picks-grid,
  .card-skeletons {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .home-section {
    margin-bottom: 48px;
  }

  .picks-grid,
  .card-skeletons {
    grid-template-columns: 1fr;
  }
}
</style>
